import React from 'react';

interface ControlPanelProps {
  isPlaying: boolean;
  speed: number;
  currentDay: number;
  initialNPCCount: number;
  onPlay: () => void;
  onPause: () => void;
  onStep: () => void;
  onReset: () => void;
  onResetWithNPCCount: (count: number) => void;
  onSpeedChange: (speed: number) => void;
  isMobile?: boolean;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  isPlaying,
  speed,
  currentDay,
  initialNPCCount,
  onPlay,
  onPause,
  onStep,
  onReset,
  onResetWithNPCCount,
  onSpeedChange,
  isMobile = false,
}) => {
  const [npcCountInput, setNpcCountInput] = React.useState(initialNPCCount);

  const handleReset = () => {
    if (npcCountInput !== initialNPCCount) {
      onResetWithNPCCount(npcCountInput);
    } else {
      onReset();
    }
  };

  const getContainerStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.container,
        flexWrap: 'wrap',
        padding: '6px 10px',
        gap: '8px',
      };
    }
    return styles.container;
  };

  const getButtonStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.button,
        padding: '4px 8px',
        fontSize: '14px',
      };
    }
    return styles.button;
  };

  const getSliderStyle = (): React.CSSProperties => {
    if (isMobile) {
      return {
        ...styles.slider,
        width: '60px',
      };
    }
    return styles.slider;
  };

  return (
    <div style={getContainerStyle()}>
      {/* 左: Day表示 */}
      <div style={isMobile ? { ...styles.dayCounter, fontSize: '14px', minWidth: '60px' } : styles.dayCounter}>
        Day {currentDay}
      </div>

      {/* 中央: コントロールボタン */}
      <div style={styles.controls}>
        {isPlaying ? (
          <button onClick={onPause} style={getButtonStyle()}>
            ⏸️
          </button>
        ) : (
          <button onClick={onPlay} style={getButtonStyle()}>
            ▶️
          </button>
        )}

        <button onClick={onStep} style={getButtonStyle()} disabled={isPlaying}>
          ⏭️
        </button>

        <div style={isMobile ? styles.npcCountControlMobile : styles.npcCountControl}>
          <label style={styles.npcLabel}>NPC:</label>
          <input
            type="number"
            min="2"
            max="50"
            value={npcCountInput}
            onChange={(e) => setNpcCountInput(Math.max(2, Math.min(50, Number(e.target.value))))}
            style={isMobile ? styles.npcInputMobile : styles.npcInput}
          />
        </div>

        <button onClick={handleReset} style={getButtonStyle()} title="リセット（NPC数を変更時は新しい数で開始）">
          🔄
        </button>
      </div>

      {/* 右: スピードスライダー */}
      <div style={styles.speedControl}>
        <input
          type="range"
          min="1"
          max="10"
          value={speed}
          onChange={(e) => onSpeedChange(Number(e.target.value))}
          style={getSliderStyle()}
        />
        <span style={isMobile ? { ...styles.speedLabel, fontSize: '11px' } : styles.speedLabel}>{speed}x</span>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '8px',
    marginBottom: '12px',
    color: 'white',
    gap: '16px',
  },
  dayCounter: {
    fontSize: '18px',
    fontWeight: 'bold',
    minWidth: '80px',
  },
  controls: {
    display: 'flex',
    gap: '6px',
  },
  button: {
    padding: '6px 12px',
    fontSize: '16px',
    cursor: 'pointer',
    border: 'none',
    borderRadius: '6px',
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
  },
  speedControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  speedLabel: {
    fontSize: '13px',
    fontWeight: 'bold',
    minWidth: '32px',
  },
  slider: {
    width: '100px',
  },
  npcCountControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '4px 8px',
    borderRadius: '6px',
  },
  npcLabel: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  npcInput: {
    width: '45px',
    padding: '4px 6px',
    fontSize: '13px',
    border: 'none',
    borderRadius: '4px',
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#333',
    textAlign: 'center',
  },
  npcCountControlMobile: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  npcInputMobile: {
    width: '40px',
    padding: '2px 4px',
    fontSize: '12px',
    border: 'none',
    borderRadius: '4px',
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#333',
    textAlign: 'center' as const,
  },
};
